import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, Plus, Trash2, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { API, PLATFORMS, getPlatformName } from "./constants";

const SettingsAlertsTab = ({ lang = "en" }) => {
  const [alerts, setAlerts] = useState([]);
  const [alertLogs, setAlertLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [newAlert, setNewAlert] = useState({ name: "", email: "", platform: "", metric: "engagement_rate", threshold: 5, condition: "below" });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [alertsRes, logsRes] = await Promise.all([
        axios.get(`${API}/alerts`),
        axios.get(`${API}/alerts/logs`),
      ]);
      setAlerts(alertsRes.data);
      setAlertLogs(logsRes.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createAlert = async () => {
    try {
      await axios.post(`${API}/alerts`, newAlert);
      toast.success(lang === "ko" ? "알림이 생성되었습니다" : lang === "zh" ? "通知已创建" : "Alert created");
      setAlertDialogOpen(false);
      setNewAlert({ name: "", email: "", platform: "", metric: "engagement_rate", threshold: 5, condition: "below" });
      fetchData();
    } catch {
      toast.error(lang === "ko" ? "알림 생성 실패" : lang === "zh" ? "创建通知失败" : "Failed to create alert");
    }
  };

  const deleteAlert = async (id) => {
    try {
      await axios.delete(`${API}/alerts/${id}`);
      toast.success(lang === "ko" ? "삭제되었습니다" : lang === "zh" ? "已删除" : "Alert deleted");
      fetchData();
    } catch {
      toast.error(lang === "ko" ? "삭제 실패" : lang === "zh" ? "删除失败" : "Failed to delete alert");
    }
  };

  const toggleAlert = async (id, enabled) => {
    try {
      await axios.patch(`${API}/alerts/${id}`, { enabled: !enabled });
      fetchData();
    } catch {
      toast.error(lang === "ko" ? "상태 변경 실패" : lang === "zh" ? "更新失败" : "Failed to update alert");
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <Card className="bg-card border-border/50">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-lg font-heading">Performance Alerts</CardTitle>
            <CardDescription>{lang === "ko" ? "지표가 임계값을 넘으면 알림을 받습니다" : lang === "zh" ? "指标超过阈值时接收通知" : "Get notified when metrics cross thresholds"}</CardDescription>
          </div>
          <Dialog open={alertDialogOpen} onOpenChange={setAlertDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="add-alert-btn"><Plus className="h-4 w-4 mr-1" /> {lang === "ko" ? "알림 추가" : lang === "zh" ? "添加通知" : "Add Alert"}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{lang === "ko" ? "알림 생성" : lang === "zh" ? "创建通知" : "Create Alert"}</DialogTitle>
                <DialogDescription>{lang === "ko" ? "새로운 성과 알림을 설정합니다" : lang === "zh" ? "设置新的性能通知" : "Set up a new performance alert"}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div><Label>{lang === "ko" ? "알림 이름" : lang === "zh" ? "通知名称" : "Alert Name"}</Label><Input value={newAlert.name} onChange={(e) => setNewAlert({ ...newAlert, name: e.target.value })} placeholder={lang === "ko" ? "예: 참여율 저하 알림" : "e.g., Low Engagement Alert"} /></div>
                <div><Label>{lang === "ko" ? "이메일" : lang === "zh" ? "邮箱" : "Email"}</Label><Input type="email" value={newAlert.email} onChange={(e) => setNewAlert({ ...newAlert, email: e.target.value })} placeholder="alert@example.com" /></div>
                <div>
                  <Label>{lang === "ko" ? "플랫폼 (선택)" : lang === "zh" ? "平台（可选）" : "Platform (optional)"}</Label>
                  <Select value={newAlert.platform} onValueChange={(v) => setNewAlert({ ...newAlert, platform: v })}>
                    <SelectTrigger><SelectValue placeholder={lang === "ko" ? "전체 플랫폼" : lang === "zh" ? "所有平台" : "All platforms"} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{lang === "ko" ? "전체 플랫폼" : lang === "zh" ? "所有平台" : "All Platforms"}</SelectItem>
                      {PLATFORMS.filter(p => p.id !== "all").map((p) => <SelectItem key={p.id} value={p.id}>{getPlatformName(p, lang)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{lang === "ko" ? "지표" : lang === "zh" ? "指标" : "Metric"}</Label>
                    <Select value={newAlert.metric} onValueChange={(v) => setNewAlert({ ...newAlert, metric: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="engagement_rate">{lang === "ko" ? "참여율" : lang === "zh" ? "参与率" : "Engagement Rate"}</SelectItem>
                        <SelectItem value="views">{lang === "ko" ? "조회수" : lang === "zh" ? "浏览量" : "Views"}</SelectItem>
                        <SelectItem value="likes">{lang === "ko" ? "좋아요" : lang === "zh" ? "点赞" : "Likes"}</SelectItem>
                        <SelectItem value="followers">{lang === "ko" ? "팔로워" : lang === "zh" ? "粉丝" : "Followers"}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{lang === "ko" ? "조건" : lang === "zh" ? "条件" : "Condition"}</Label>
                    <Select value={newAlert.condition} onValueChange={(v) => setNewAlert({ ...newAlert, condition: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="below">{lang === "ko" ? "미만" : lang === "zh" ? "低于" : "Below"}</SelectItem>
                        <SelectItem value="above">{lang === "ko" ? "이상" : lang === "zh" ? "高于" : "Above"}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>{lang === "ko" ? "임계값" : lang === "zh" ? "阈值" : "Threshold"}</Label><Input type="number" value={newAlert.threshold} onChange={(e) => setNewAlert({ ...newAlert, threshold: parseFloat(e.target.value) })} /></div>
              </div>
              <DialogFooter><Button onClick={createAlert}>{lang === "ko" ? "알림 생성" : lang === "zh" ? "创建通知" : "Create Alert"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {alerts.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">{lang === "ko" ? "아직 알림이 설정되지 않았습니다" : lang === "zh" ? "暂未配置通知" : "No alerts configured yet"}</p>
          ) : (
            <div className="space-y-3">
              {alerts.map((alert) => (
                <div key={alert.id} className="flex flex-col gap-3 rounded-lg border border-border/50 bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-4">
                    <Switch checked={alert.enabled} onCheckedChange={() => toggleAlert(alert.id, alert.enabled)} />
                    <div className="min-w-0">
                      <p className="truncate font-medium">{alert.name}</p>
                      <p className="text-sm text-muted-foreground">{alert.metric} {alert.condition} {alert.threshold}{alert.platform && ` (${alert.platform})`}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => deleteAlert(alert.id)} className="w-full sm:w-auto"><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-lg font-heading">{lang === "ko" ? "알림 이력" : lang === "zh" ? "通知历史" : "Alert History"}</CardTitle>
          <CardDescription>{lang === "ko" ? "최근 알림 기록" : lang === "zh" ? "最近的通知记录" : "Recent alert notifications"}</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[300px]">
            {alertLogs.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">{lang === "ko" ? "알림 이력이 없습니다" : lang === "zh" ? "暂无通知历史" : "No alert history"}</p>
            ) : (
              <div className="space-y-2">
                {alertLogs.map((log) => (
                  <div key={log.id} className="flex flex-col gap-2 rounded-lg border border-border/30 bg-muted/20 p-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                      <Bell className={`h-4 w-4 ${log.status === "sent" ? "text-emerald-500" : "text-amber-500"}`} />
                      <div className="min-w-0"><p className="truncate text-sm font-medium">{log.alert_name}</p><p className="text-xs text-muted-foreground">{log.message}</p></div>
                    </div>
                    <span className="text-xs text-muted-foreground">{new Date(log.triggered_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsAlertsTab;
